import { usePathname } from 'next/navigation'
import { useMemo } from 'react'

import { Permission } from '@/types/schema/permissions'
import { UserArchetype } from '@/utils/trade-blotter/compute-order-status'

import useHasPermissions from './use-has-permissions'

/**
 * Hook to determine user archetype (heuristic) based on the current pathname
 *
 * @returns UserArchetype - Returns UserArchetype.Trader for trade-desk paths and OMSOrderCreate permission,
 *                          UserArchetype.Advisor for all other paths
 */
export function useUserArchetype(): UserArchetype {
  const pathname = usePathname()
  // TODO - possibly handle new failure mode where getSelf can fail - better to handle this server side
  // `undefined` while useGetSelfQuery is loading falls through to Advisor. OK in practice because
  // OrderEntry mounts lazily after the same query has gated its entry button.
  const canCreateOrder = useHasPermissions([Permission.OMSOrderCreate])

  const userArchetype = useMemo(() => {
    // Heuristic: If the user has order creation permissions or is on a trade-desk path, classify as Trader
    if (pathname.includes('/trade-desk/') || canCreateOrder) {
      return UserArchetype.Trader
    }
    return UserArchetype.Advisor
  }, [pathname, canCreateOrder])

  return userArchetype
}

// TODO - remove. Patch for some legacy usage after the above change.
// Long-term we should remove any UI gating on brittle heuristic archetype detection.
export function useLegacyUserArchetype(): UserArchetype {
  const pathname = usePathname()

  return useMemo(() => {
    // Heuristic: If the user has order creation permissions or is on a trade-desk path, classify as Trader
    if (pathname.includes('/trade-desk/')) {
      return UserArchetype.Trader
    }
    return UserArchetype.Advisor
  }, [pathname])
}
